/**
 * Reputation: a hidden float in [-1, 1] that a player only ever sees as the
 * state of their own seal.
 *
 * The score is never shown as a number, and neither are the counts behind it
 * (Q5.2): two numbers and a little arithmetic reconstruct the score the rules
 * say a player may never see. Nothing in this module returns anything a screen
 * could print except a tier word.
 *
 * Reputation is visible to the game in exactly two places: the mood bucket a
 * code name is drawn from, and the player's own seal.
 */

import { GAME_CONFIG, REPUTATION_TIERS, type ReputationTier } from './game-config.ts'

const { reputation } = GAME_CONFIG

/**
 * What a recipient did with a letter. `auto_false` is the automatic
 * contradiction -- the recipient walked a tile that disagreed with what the
 * letter charted -- and it costs the sender the same as being disbelieved,
 * because it is the same claim being wrong (`docs/rules.md` section 6).
 */
export const MARKS = ['trustworthy', 'false', 'auto_false'] as const
export type Mark = (typeof MARKS)[number]

/** Keep a score inside the bounds the rules give it. */
export function clampScore(score: number): number {
  if (!Number.isFinite(score)) return reputation.start
  return Math.min(reputation.max, Math.max(reputation.min, score))
}

/** The change a mark makes to a sender's score. */
export function markDelta(mark: Mark): number {
  return mark === 'trustworthy' ? reputation.onTrustworthy : reputation.onFalse
}

/** Apply one verdict to a sender's score. */
export function applyMark(score: number, mark: Mark): number {
  return clampScore(clampScore(score) + markDelta(mark))
}

/**
 * One night's decay, applied once per day by the daily job.
 *
 * Multiplying pulls a score toward zero from either side, so a reputation is
 * something kept up rather than banked: an honest player who stops playing
 * fades back to unproven, and so does a liar.
 */
export function decay(score: number): number {
  return clampScore(clampScore(score) * reputation.nightlyDecay)
}

/** The tier a score sits in. The only thing about a score anyone ever sees. */
export function tierOf(score: number): ReputationTier {
  const bounded = clampScore(score)
  if (bounded >= reputation.tierThresholds.trusted) return 'trusted'
  if (bounded >= reputation.tierThresholds.known) return 'known'
  if (bounded >= reputation.tierThresholds.unproven) return 'unproven'
  return 'doubted'
}

/** The `guild-voice.json` ui key describing a seal in this tier. */
export function sealKey(tier: ReputationTier): string {
  return `seal${tier.charAt(0).toUpperCase()}${tier.slice(1)}`
}

export { REPUTATION_TIERS }
